import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { themeable } from '@instructure/ui-themeable';
import { omitProps, callRenderProp } from '@instructure/ui-react-utils';
import { IconMiniArrowUpLine, IconMiniArrowDownLine } from '@instructure/ui-icons';
var styles = {
  componentId: 'fwfoD',
  template: function template(theme) {
    return "\n\n.fwfoD_bGBk{background:".concat(theme.background || 'inherit', ";box-sizing:border-box;vertical-align:middle}\n\n.fwfoD_fsuY{color:").concat(theme.color || 'inherit', ";font-family:").concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.fontSize || 'inherit', ";font-weight:700;line-height:").concat(theme.lineHeight || 'inherit', ";padding:").concat(theme.padding || 'inherit', "}\n\n.fwfoD_EMjX{text-align:start}\n\n[dir=ltr] .fwfoD_EMjX{text-align:left}\n\n[dir=rtl] .fwfoD_EMjX{text-align:right}\n\n.fwfoD_ImeN,[dir=ltr] .fwfoD_ImeN,[dir=rtl] .fwfoD_ImeN{text-align:center}\n\n.fwfoD_dBtH{text-align:end}\n\n[dir=ltr] .fwfoD_dBtH{text-align:right}\n\n[dir=rtl] .fwfoD_dBtH{text-align:left}\n\n.fwfoD_bRML{flex-direction:row}\n\n.fwfoD_fEuk{justify-content:center}\n\n.fwfoD_ftfl{flex-direction:row-reverse}\n\n.fwfoD_EwaR{align-items:center;background:transparent;border:none;box-sizing:border-box;cursor:pointer;display:flex;height:100%;outline:none;width:100%}\n\n.fwfoD_EwaR::-moz-focus-inner{border:0}\n\n.fwfoD_EwaR:focus{outline:").concat(theme.focusOutlineWidth || 'inherit', " ").concat(theme.focusOutlineStyle || 'inherit', " ").concat(theme.focusOutlineColor || 'inherit', "}");
  },
  'root': 'fwfoD_bGBk',
  'header': 'fwfoD_fsuY',
  'textAlign--start': 'fwfoD_EMjX',
  'textAlign--center': 'fwfoD_ImeN',
  'textAlign--end': 'fwfoD_dBtH',
  'flexDirection--start': 'fwfoD_bRML',
  'flexDirection--center': 'fwfoD_fEuk',
  'flexDirection--end': 'fwfoD_ftfl',
  'button': 'fwfoD_EwaR'
};
import theme from './theme';
/**
---
parent: Table
id: Table.ColHeader
---
**/

var _ref = /*#__PURE__*/React.createElement(IconMiniArrowUpLine, null);

var _ref2 = /*#__PURE__*/React.createElement(IconMiniArrowDownLine, null);

var ColHeader = (_dec = themeable(theme, styles), _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(ColHeader, _Component);

  var _super = _createSuper(ColHeader);

  function ColHeader() {
    var _this;

    _classCallCheck(this, ColHeader);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _this.handleClick = function (event) {
      var _this$props = _this.props,
          id = _this$props.id,
          onRequestSort = _this$props.onRequestSort;
      onRequestSort(event, {
        id: id
      });
    };

    return _this;
  }

  _createClass(ColHeader, [{
    key: "renderSortArrow",
    value: function renderSortArrow() {
      var _this$props2 = this.props,
          sortDirection = _this$props2.sortDirection,
          onRequestSort = _this$props2.onRequestSort;

      if (sortDirection === 'ascending') {
        return _ref;
      }

      if (sortDirection === 'descending') {
        return _ref2;
      }

      if (onRequestSort) {
        // Reserve a space for arrow to keep column width consistent
        return /*#__PURE__*/React.createElement(IconMiniArrowUpLine, {
          style: {
            color: 'transparent'
          }
        });
      }

      return null;
    }
  }, {
    key: "render",
    value: function render() {
      var _classnames, _classnames2;

      var _this$props3 = this.props,
          onRequestSort = _this$props3.onRequestSort,
          width = _this$props3.width,
          textAlign = _this$props3.textAlign,
          children = _this$props3.children,
          sortDirection = _this$props3.sortDirection;
      return /*#__PURE__*/React.createElement("th", Object.assign({}, omitProps(this.props, ColHeader.propTypes), {
        className: classnames((_classnames = {}, _defineProperty(_classnames, styles.root, true), _defineProperty(_classnames, styles.header, !onRequestSort), _defineProperty(_classnames, styles["textAlign--".concat(textAlign)], true), _classnames)),
        style: {
          width: width
        },
        scope: "col",
        "aria-sort": sortDirection
      }), onRequestSort && /*#__PURE__*/React.createElement("button", {
        onClick: this.handleClick,
        className: classnames((_classnames2 = {}, _defineProperty(_classnames2, styles.header, true), _defineProperty(_classnames2, styles.button, true), _defineProperty(_classnames2, styles["flexDirection--".concat(textAlign)], true), _classnames2))
      }, /*#__PURE__*/React.createElement("div", null, callRenderProp(children), this.renderSortArrow())), !onRequestSort && children, !onRequestSort && this.renderSortArrow());
    }
  }]);

  ColHeader.displayName = "ColHeader";
  return ColHeader;
}(Component), _class2.propTypes = {
  /**
   * A unique id for this column. When sortable table is in stacked layout,
   * id is also used as option in combobox.
   */
  id: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * Control the width of column.
   */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Control the text alignment in column header
   */
  textAlign: PropTypes.oneOf(['start', 'center', 'end']),

  /**
   * The string of sorting direction
   */
  sortDirection: PropTypes.oneOf(['none', 'ascending', 'descending']),

  /**
   * Callback fired when column header is clicked. Parameters: `(event, { id })`
   */
  onRequestSort: PropTypes.func
}, _class2.defaultProps = {
  textAlign: 'start',
  sortDirection: 'none',
  children: null
}, _temp)) || _class);
export default ColHeader;
export { ColHeader };